/**
 * @file     consent.ts
 * @readme   ../CLAUDE.md
 * @purpose  The stored cookie choice (relta_consent): one format shared by the
 *           inline boot scripts, the browser runtime, middleware and the server
 *           conversion legs. Bumping CONSENT_VERSION re-prompts every visitor.
 */
import { SIGNATURE_PATH_PATTERN } from '@/core/config/signaturePaths';

export const CONSENT_COOKIE = 'relta_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_S = 60 * 60 * 24 * 365;

export interface ConsentChoice {
  analytics: boolean;
  marketing: boolean;
  /** Unix seconds when the visitor chose. The 12-month expiry runs from here. */
  decidedAt: number;
  /** Re-issued over HTTP by middleware, so Safari ITP's seven-day cap on script cookies no longer applies. */
  serverStamped: boolean;
}

const CONSENT_RE = /^(\d+)\.([01])\.([01])\.(\d+)(\.s)?$/;

/** `<version>.<analytics>.<marketing>.<decidedAt>[.s]` */
export function serializeConsent(choice: Pick<ConsentChoice, 'analytics' | 'marketing' | 'decidedAt'>, serverStamped = false): string {
  return `${CONSENT_VERSION}.${choice.analytics ? 1 : 0}.${choice.marketing ? 1 : 0}.${choice.decidedAt}${serverStamped ? '.s' : ''}`;
}

/** A choice made under an older policy version reads as no choice, which re-prompts. */
export function parseConsent(raw: string | null | undefined): ConsentChoice | null {
  if (!raw) return null;
  const m = CONSENT_RE.exec(raw.trim());
  if (!m || Number(m[1]) !== CONSENT_VERSION) return null;
  return {
    analytics: m[2] === '1',
    marketing: m[3] === '1',
    decidedAt: Number(m[4]),
    serverStamped: m[5] === '.s',
  };
}

/** Seconds of the 12-month window left for a choice made at `decidedAt`. */
export function consentMaxAgeLeft(decidedAt: number, nowS = Math.floor(Date.now() / 1000)): number {
  return Math.max(0, decidedAt + CONSENT_MAX_AGE_S - nowS);
}

/** True only when the visitor explicitly turned analytics off. No choice keeps today's defaults. */
export function analyticsConsentDenied(raw: string | null | undefined): boolean {
  return parseConsent(raw)?.analytics === false;
}

/**
 * Visitor region stamped by middleware from Vercel's geo headers, as
 * `<country>-<subdivision>` (e.g. CA-QC). Only opt-in regions get the cookie,
 * so its absence means the existing defaults.
 */
export const REGION_COOKIE = 'relta_rgn';
export const REGION_MAX_AGE_S = 60 * 60 * 24 * 30;
/** Quebec Law 25: analytics and advertising stay off until the visitor accepts. */
export const OPT_IN_REGIONS: readonly string[] = ['CA-QC'];

/** `CA-QC` from Vercel's x-vercel-ip-country / x-vercel-ip-country-region, or null. */
export function visitorRegion(country: string | null | undefined, subdivision: string | null | undefined): string | null {
  const c = country?.trim().toUpperCase();
  const s = subdivision?.trim().toUpperCase();
  return c && s ? `${c}-${s}` : null;
}

export function isOptInRegion(region: string | null | undefined): boolean {
  return Boolean(region && OPT_IN_REGIONS.includes(region));
}

/** Reads relta_rgn out of a Cookie header or document.cookie string. */
export function optInRegionFromCookies(cookieString: string | null | undefined): boolean {
  const m = (cookieString ?? '').match(new RegExp(`(?:^|;\\s*)${REGION_COOKIE}=([^;]*)`));
  return isOptInRegion(m ? m[1] : null);
}

/**
 * True when nothing may track yet: an explicit analytics "no", or an opt-in
 * region visitor who has not chosen. Read from a Cookie header.
 */
export function analyticsBlockedByCookies(cookieString: string | null | undefined): boolean {
  const m = (cookieString ?? '').match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE}=([^;]*)`));
  const choice = parseConsent(m ? m[1] : null);
  if (choice) return !choice.analytics;
  return optInRegionFromCookies(cookieString);
}

/**
 * ES5 snippet for the inline boot scripts. Declares `reltaConsent` as
 * `{a:boolean,m:boolean}` for a current-version choice, or null, and
 * `reltaOptIn` (opt-in region, no tracking before a choice). Both boot
 * scripts inline this one definition so they can never read the cookie
 * differently.
 */
/**
 * Signing pages count as opt-in: the site notice does not show there, so the
 * implied-consent default that leans on it does not apply either.
 */
export function consentReaderSnippet(): string {
  return `var reltaConsent=null,reltaOptIn=false;try{var rcm=document.cookie.match(/(?:^|; )${CONSENT_COOKIE}=(\\d+)\\.([01])\\.([01])\\./);if(rcm&&rcm[1]==='${CONSENT_VERSION}'){reltaConsent={a:rcm[2]==='1',m:rcm[3]==='1'};}var rgm=document.cookie.match(/(?:^|; )${REGION_COOKIE}=([A-Z-]+)/);reltaOptIn=!!(rgm&&${JSON.stringify(OPT_IN_REGIONS)}.indexOf(rgm[1])!==-1);if(${SIGNATURE_PATH_PATTERN.toString()}.test(location.pathname))reltaOptIn=true;}catch(e){/* cookies blocked, defaults apply; fire-and-forget, intentional */}`;
}
